import { Component, ojs, app, state, v, payload } from "modular-openscriptjs";
import { dom, tool } from "../../fotastart";
import { eParse, cleanDispose } from "../../helpers";

const h = app("h");
const broker = app("broker");
const $e = app("$e");

// How long a star click waits, "loading down" to zero, before it's
// actually saved - clicking a different star during this window just
// restarts the countdown (that's the "debounce").
const COUNTDOWN_SECONDS = 3;
const RING_RADIUS = 15;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export default class BusinessRatingModal extends Component {
    modal = null;
    items = state([]);

    // order_item_id -> setTimeout id, for items currently mid-countdown.
    timers = {};
    // order_item_id -> { item, rating }, the last rating chosen for an
    // item that's mid-countdown (or was just submitted).
    pending = {};

    render(...args) {
        return h.div({ class: "d-none" }, ...args);
    }

    $$businessRating = {
        needs: {
            prompt: (ed, event) => this.showForm(ed, event),
        },
        itemRated: (ed, event) => this.onItemRated(ed, event),
        itemRatingFailed: (ed, event) => this.onItemRatingFailed(ed, event),
    };

    showForm(ed, event) {
        const { message } = eParse(ed);
        const items = message.get("items", []);

        if (!Array.isArray(items) || items.length === 0) return;

        this.cancelAllTimers();
        this.pending = {};
        this.items.value = items;

        if (this.modal) {
            cleanDispose(this.modal);
        }

        const content = h.div(
            {
                class: "modal-content border-0 rounded-4 shadow-lg overflow-hidden",
            },
            this.styles(),
            h.div(
                { class: "modal-body p-4" },
                this.renderHeader(),
                this.renderList(),
            ),
        );

        this.modal = tool.plainModal(content, {});

        this.modal._element?.addEventListener("hidden.bs.modal", () => {
            // Don't drop a rating the customer already tapped just because
            // they closed the sheet before the countdown finished.
            this.flushPendingTimers();
            broker.send($e.businessRating.dismissed);
        });

        this.modal.show();
        broker.send($e.businessRating.shown);
    }

    renderHeader() {
        return h.div(
            { class: "d-flex justify-content-between align-items-start mb-3" },
            h.div(
                h.h5({ class: "fw-bold mb-1" }, "Rate your order"),
                h.p(
                    { class: "text-muted small mb-0" },
                    "Tap a star for each item below - we'll save it automatically.",
                ),
            ),
            h.button({
                class: "btn-close bg-light rounded-circle p-2 flex-shrink-0",
                "data-bs-dismiss": "modal",
                aria_label: "Close",
            }),
        );
    }

    renderList() {
        return v(this.items, (itemsState) => {
            const items = itemsState.value || [];

            if (items.length === 0) {
                return h.div(
                    { class: "text-center py-4" },
                    h.i({
                        class: "fas fa-circle-check text-success mb-2",
                        style: "font-size: 2rem;",
                    }),
                    h.p(
                        { class: "mb-0 fw-semibold text-dark" },
                        "All done, thank you!",
                    ),
                );
            }

            return h.div(
                { class: "d-flex flex-column gap-2" },
                items.map((item) => this.renderItemRow(item)),
            );
        });
    }

    renderItemRow(item) {
        const id = item.order_item_id;
        const logo = item.company?.logo;
        const initialRating = 0;

        return h.div(
            {
                id: `br-row-${id}`,
                class: "br-row d-flex align-items-center gap-3 p-3 rounded-4 bg-light",
            },
            logo
                ? h.img({
                      src: logo,
                      class: "rounded-circle flex-shrink-0 bg-white",
                      style: "width: 44px; height: 44px; object-fit: cover;",
                      alt: item.company?.name || "",
                  })
                : h.div(
                      {
                          class: "rounded-circle bg-secondary-subtle d-flex align-items-center justify-content-center flex-shrink-0",
                          style: "width: 44px; height: 44px;",
                      },
                      h.i({ class: "fas fa-store text-secondary" }),
                  ),
            h.div(
                { class: "flex-grow-1 min-w-0" },
                h.div(
                    { class: "small text-muted" },
                    item.company?.name || "",
                ),
                h.div(
                    { class: "fw-semibold text-dark" },
                    `How was ${item.product_name || "this product"}?`,
                ),
                h.div(
                    { id: `br-stars-${id}`, class: "d-flex gap-1 mt-1" },
                    [1, 2, 3, 4, 5].map((r) =>
                        h.span(
                            {
                                class: "br-star",
                                data_star: r,
                                onclick: this.method("rate", [item, r]),
                            },
                            h.i({
                                class:
                                    r <= initialRating
                                        ? "fas fa-star text-warning"
                                        : "far fa-star text-secondary-subtle",
                            }),
                        ),
                    ),
                ),
                h.div({
                    id: `br-error-${id}`,
                    class: "small text-danger mt-1 d-none",
                }),
            ),
            h.div(
                { id: `br-ring-${id}`, class: "br-ring flex-shrink-0 d-none" },
                h.$svg(
                    { viewBox: "0 0 36 36", width: "26", height: "26" },
                    h.$circle({
                        cx: "18",
                        cy: "18",
                        r: `${RING_RADIUS}`,
                        fill: "none",
                        stroke: "#dee2e6",
                        stroke_width: "3",
                    }),
                    h.$circle({
                        class: "br-ring-progress",
                        cx: "18",
                        cy: "18",
                        r: `${RING_RADIUS}`,
                        fill: "none",
                        stroke: "#0d6efd",
                        stroke_width: "3",
                        stroke_linecap: "round",
                        stroke_dasharray: `${RING_CIRCUMFERENCE}`,
                        stroke_dashoffset: "0",
                        transform: "rotate(-90 18 18)",
                    }),
                ),
            ),
        );
    }

    styles() {
        return h.style(`
            .br-star {
                cursor: pointer;
                font-size: 1.15rem;
                line-height: 1;
            }
            .br-ring-progress {
                transition: stroke-dashoffset ${COUNTDOWN_SECONDS}s linear;
            }
            .br-row {
                transition: opacity .25s ease, max-height .25s ease,
                    margin .25s ease, padding .25s ease;
                overflow: hidden;
                max-height: 120px;
            }
            .br-row.br-row-removing {
                opacity: 0;
                max-height: 0;
                padding-top: 0 !important;
                padding-bottom: 0 !important;
                margin: 0 !important;
                border-width: 0 !important;
            }
        `);
    }

    rate(item, rating) {
        const id = item.order_item_id;

        this.setStarsDisplay(id, rating);
        this.hideError(id);
        this.restartRing(id);

        this.pending[id] = { item, rating };

        if (this.timers[id]) {
            clearTimeout(this.timers[id]);
        }

        this.timers[id] = setTimeout(() => {
            delete this.timers[id];
            this.commitRating(id);
        }, COUNTDOWN_SECONDS * 1000);
    }

    setStarsDisplay(id, rating) {
        const container = dom.id(`br-stars-${id}`);
        if (!container) return;

        container.querySelectorAll(".br-star").forEach((star) => {
            const r = Number(star.getAttribute("data-star"));
            const icon = star.querySelector("i");
            if (!icon) return;

            if (r <= rating) {
                icon.className = "fas fa-star text-warning";
            } else {
                icon.className = "far fa-star text-secondary-subtle";
            }
        });
    }

    restartRing(id) {
        const wrapper = dom.id(`br-ring-${id}`);
        if (!wrapper) return;

        wrapper.classList.remove("d-none");

        const progress = wrapper.querySelector(".br-ring-progress");
        if (!progress) return;

        // Snap back to "full" with no transition, force a reflow, then
        // re-enable the transition and animate down to empty - this is
        // what lets re-tapping a star reset the countdown cleanly.
        progress.style.transition = "none";
        progress.style.strokeDashoffset = "0";
        // eslint-disable-next-line no-unused-expressions
        progress.getBoundingClientRect();
        progress.style.transition = `stroke-dashoffset ${COUNTDOWN_SECONDS}s linear`;
        progress.style.strokeDashoffset = `${RING_CIRCUMFERENCE}`;
    }

    hideRing(id) {
        const wrapper = dom.id(`br-ring-${id}`);
        if (wrapper) wrapper.classList.add("d-none");
    }

    showError(id, message) {
        const el = dom.id(`br-error-${id}`);
        if (!el) return;
        el.textContent = message;
        el.classList.remove("d-none");
    }

    hideError(id) {
        const el = dom.id(`br-error-${id}`);
        if (!el) return;
        el.classList.add("d-none");
        el.textContent = "";
    }

    commitRating(id) {
        const entry = this.pending[id];
        if (!entry) return;

        const { item, rating } = entry;

        broker.send(
            $e.businessRating.needs.rateItem,
            payload({
                orderItemId: id,
                companyId: item.company?.id,
                rating,
            }),
        );
    }

    onItemRated(ed, event) {
        const { message } = eParse(ed);
        const id = message.get("orderItemId");

        delete this.pending[id];
        this.hideRing(id);
        this.removeRow(id);
    }

    onItemRatingFailed(ed, event) {
        const { message } = eParse(ed);
        const id = message.get("orderItemId");

        this.hideRing(id);
        this.showError(id, "Couldn't save that rating - tap a star to try again.");
    }

    removeRow(id) {
        const row = dom.id(`br-row-${id}`);
        if (row) {
            row.classList.add("br-row-removing");
        }

        setTimeout(() => {
            this.items.value = this.items.value.filter(
                (i) => i.order_item_id !== id,
            );

            if (this.items.value.length === 0) {
                setTimeout(() => {
                    if (this.modal) this.modal.hide();
                }, 1200);
            }
        }, 260);
    }

    cancelAllTimers() {
        for (let id in this.timers) {
            clearTimeout(this.timers[id]);
        }
        this.timers = {};
    }

    // Instead of discarding an in-flight (mid-countdown) rating when the
    // modal closes, save it right away - the customer already made their
    // choice, closing the sheet shouldn't lose it.
    flushPendingTimers() {
        for (let id in this.timers) {
            clearTimeout(this.timers[id]);
            delete this.timers[id];
            this.commitRating(id);
        }
    }

    cleanUp() {
        super.cleanUp();
        this.cancelAllTimers();
        if (this.modal) cleanDispose(this.modal);
    }
}

ojs(BusinessRatingModal);
